import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet, apiPostJson, apiPutJson, ApiError } from '../api/client';
import { todayIso } from '../lib/format';

/**
 * Add/edit a planned expense. Ports frm{Business}PlanifikuaraPages.
 *
 * Creating and editing are genuinely different operations here, which the
 * desktop dialog handled in one code path and this reflects in the UI:
 *
 *  - Creating generates a SERIES — one row per period between the start and
 *    end dates (frmStargymPlanifikuaraPages.cs:262-290).
 *  - Editing changes a SINGLE row, and deliberately never touches `periudha`,
 *    because the period describes the series the row belongs to (line 236).
 */

interface Article {
  id: number;
  artikulli: string;
}

interface PlannedDetail {
  ID: number;
  data: string;
  artikulliName: string | null;
  cmimi: number;
  periudha: string | null;
}

interface Props {
  biznesi: string;
  businessName: string;
  kategoriaId: number;
  categoryName: string;
  plannedId: number | null;
  onClose: () => void;
  onSaved: (message: string) => void;
}

/** Default end date for a new series: a year out, so the range is never empty. */
function yearAheadIso(): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
}

export default function PlannedModal({
  biznesi,
  businessName,
  kategoriaId,
  categoryName,
  plannedId,
  onClose,
  onSaved,
}: Props) {
  const isEdit = plannedId !== null;

  const [data, setData] = useState(todayIso);
  const [endDate, setEndDate] = useState(yearAheadIso);
  const [artikulli, setArtikulli] = useState('');
  const [cmimi, setCmimi] = useState('');
  const [periudha, setPeriudha] = useState('1 Muaj');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const optionsQuery = useQuery({
    queryKey: ['planned-articles', biznesi, kategoriaId],
    queryFn: () =>
      apiGet<{ articles: Article[]; periudhaOptions: string[] }>(
        `/shpenzimet/planifikuara/${biznesi}/artikujt`,
        { kategoriaId },
      ),
  });

  const detailQuery = useQuery({
    queryKey: ['planned', plannedId],
    queryFn: () => apiGet<PlannedDetail>(`/shpenzimet/planifikuara/shpenzimi/${plannedId}`),
    enabled: isEdit,
  });

  useEffect(() => {
    const d = detailQuery.data;
    if (!d) return;
    setData(d.data.slice(0, 10));
    setArtikulli(d.artikulliName ?? '');
    setCmimi(String(d.cmimi ?? ''));
    setPeriudha(d.periudha ?? '');
  }, [detailQuery.data]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!artikulli.trim()) {
      setError('Ju lutem zgjidhni një artikull.');
      return;
    }
    const price = Number(cmimi);
    if (cmimi.trim() === '' || !Number.isFinite(price)) {
      setError('Ju lutem vendosni një çmim të vlefshëm.');
      return;
    }
    if (!isEdit) {
      if (!periudha) {
        setError('Ju lutem zgjidhni një periudhë.');
        return;
      }
      if (endDate <= data) {
        setError('Data e mbarimit duhet të jetë më e madhe se data e fillimit.');
        return;
      }
    }

    setSaving(true);
    try {
      if (isEdit) {
        await apiPutJson(`/shpenzimet/planifikuara/${biznesi}/shpenzimet/${plannedId}`, {
          kategoriaId,
          data,
          artikulli: artikulli.trim(),
          cmimi: price,
        });
        onSaved('Të dhënat u përditësuan me sukses!');
      } else {
        const result = await apiPostJson<{ inserted: number; warning?: string }>(
          `/shpenzimet/planifikuara/${biznesi}/shpenzimet`,
          { kategoriaId, data, endDate, artikulli: artikulli.trim(), cmimi: price, periudha },
        );
        onSaved(
          result.warning ?? `U krijuan ${result.inserted} rreshta të planifikuar.`,
        );
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Ruajtja dështoi. Provoni përsëri.');
    } finally {
      setSaving(false);
    }
  }

  const periudhaOptions = optionsQuery.data?.periudhaOptions ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center sm:p-4">
      <form
        onSubmit={handleSubmit}
        className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-white p-5 shadow-2xl sm:rounded-2xl"
      >
        <div className="mb-4">
          <h2 className="text-lg font-semibold">
            {isEdit ? 'Ndrysho shpenzimin e planifikuar' : 'Planifiko shpenzim'}
          </h2>
          <p className="text-sm text-muted">
            {businessName} — {categoryName}
          </p>
        </div>

        {!isEdit && (
          <p className="mb-4 rounded-lg bg-brand-50 px-3 py-2 text-sm text-brand-700">
            Do të krijohet një rresht për çdo periudhë nga data e fillimit deri te data e
            mbarimit.
          </p>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="p-data">
              {isEdit ? 'Data' : 'Data e fillimit'}
            </label>
            <input
              id="p-data"
              type="date"
              value={data}
              onChange={(e) => setData(e.target.value)}
              required
              className="w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
            />
          </div>

          {!isEdit && (
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="p-end">
                Data e mbarimit
              </label>
              <input
                id="p-end"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                required
                className="w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
              />
            </div>
          )}

          <div className="sm:col-span-2">
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="p-artikulli">
              Artikulli
            </label>
            {/* Closed list — frmStargymPlanifikuaraPages.Designer.cs:121. */}
            <select
              id="p-artikulli"
              value={artikulli}
              onChange={(e) => setArtikulli(e.target.value)}
              required
              className="w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
            >
              <option value="">Zgjidhni një artikull</option>
              {optionsQuery.data?.articles.map((a) => (
                <option key={a.id} value={a.artikulli}>
                  {a.artikulli}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="p-cmimi">
              Çmimi për periudhë
            </label>
            <input
              id="p-cmimi"
              inputMode="decimal"
              value={cmimi}
              onChange={(e) => setCmimi(e.target.value.replace(/[^\d.]/g, ''))}
              required
              className="tnum w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="p-periudha">
              Periudha
            </label>
            <select
              id="p-periudha"
              value={periudha}
              onChange={(e) => setPeriudha(e.target.value)}
              disabled={isEdit}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100 disabled:bg-slate-50 disabled:text-muted"
            >
              <option value="">—</option>
              {periudhaOptions.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
            {isEdit && (
              <p className="mt-1 text-xs text-muted">
                Periudha i përket serisë dhe nuk ndryshohet për një rresht të vetëm.
              </p>
            )}
          </div>
        </div>

        {error && (
          <div role="alert" className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Anulo
          </button>
          <button
            type="submit"
            disabled={saving}
            className="rounded-lg bg-brand-500 px-5 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-60"
          >
            {saving ? 'Duke ruajtur…' : 'Ruaj'}
          </button>
        </div>
      </form>
    </div>
  );
}
